import React, { Fragment, memo, useContext } from "react";
import { Drawer } from "@material-ui/core";

// context
import { DispatchContext, StateContext } from "bodycodi/cmap/app/components/common/context";

// components
import Input from "./Input";

// assets
import bpay_icon from "bodycodi/cmap/app/svg/bpay_icon.svg";
import coach_default from "bodycodi/cmap/app/img/member-profile-empty.png";
import point_info from "bodycodi/cmap/app/svg/point_info_icon.svg";

// utils
import {
  getServiceType,
  getUseNumber,
  getUsePeriod,
  moneyFormatter,
  recoverTag,
} from "bodycodi/cmap/app/js/common";
import { padTo2Digits } from "bodycodi/cmap/app/js/date";

const ConfirmPopup = ({
  passDetails,
  packageInfo,
  selectedDates,
  discount,
  onDiscountChange,
  handleSubmit,
}: any) => {
  const { state } = useContext(StateContext);
  const { dispatch } = useContext(DispatchContext);

  const handleClose = () => {
    dispatch({
      type: "POP_UP_OPEN",
      id: "REQUEST_APP_PAYMENT_CONFIRM_POP",
      open: { requestAppPaymentConfirmPop: false },
    });
    dispatch({
      type: "POP_UP_OPEN",
      id: "REQUEST_APP_PAYMENT_POP",
      open: { requestAppPaymentPop: false },
    });
  };

  return (
    <Drawer
      anchor="bottom"
      open={state.requestAppPaymentConfirmPop}
      onClose={handleClose}
      className="picker-pop auto"
    >
      <div className="request-confirm-pop">
        <UserInfo details={passDetails} />
        <PassInfo details={passDetails} packageInfo={packageInfo} selectedDates={selectedDates} />
        <PriceInfo
          details={passDetails}
          packageInfo={packageInfo}
          discount={discount}
          onDiscountChange={onDiscountChange}
        />

        <div className="buttons-container">
          <button className="cancel" onClick={handleClose}>
            취소
          </button>
          <button className="submit" onClick={handleSubmit}>
            <img src={bpay_icon} />
            앱결제 요청
          </button>
        </div>
      </div>
    </Drawer>
  );
};

const UserInfo = memo(({ details }: any) => {
  return (
    <div className="user-info">
      <img className="profile" src={details.userProfileImg || coach_default} />
      <span>{`${details.userName ? details.userName : "회원"}님께`}</span>
      <span>
        <span className="blue">비페이 결제 요청</span>할까요?
      </span>
    </div>
  );
});

const PassInfo = memo(({ details, packageInfo, selectedDates }: any) => {
  if (details.packagePass === "Y") {
    const appointment = packageInfo.passList.find((el: any) => el.serviceType === "APPOINTMENT");
    return (
      <div className="service-info package">
        <div className="header">
          <span className="badge">패키지</span>
          <span className="title">{recoverTag(packageInfo?.packageName)}</span>
        </div>

        <div className="service-names-wrapper">
          <span className="additional-info">{packageInfo?.serviceNames.join(" + ")}</span>
        </div>

        {appointment && (
          <Fragment>
            <div className="divider" />
            <div className="wrapper">
              <span className="gray">담당강사</span>
              <span className="dark">{appointment?.coachName}</span>
            </div>
            <div className="wrapper">
              <span className="gray">장소</span>
              <span className="dark">{appointment?.spaceName}</span>
            </div>
          </Fragment>
        )}
      </div>
    );
  }

  const formatPassDate = () => {
    if (
      !selectedDates[details?.seqService]?.startDate ||
      !selectedDates[details?.seqService]?.endDate
    ) {
      return "-";
    }

    const startDate = new Date(selectedDates[details?.seqService]?.startDate);
    const endDate = new Date(selectedDates[details?.seqService]?.endDate);

    const start =
      startDate.getFullYear().toString().slice(-2) +
      "." +
      padTo2Digits(startDate.getMonth() + 1) +
      "." +
      padTo2Digits(startDate.getDate());

    const end =
      endDate.getFullYear().toString().slice(-2) +
      "." +
      padTo2Digits(endDate.getMonth() + 1) +
      "." +
      padTo2Digits(endDate.getDate());

    return `${start} - ${end}`;
  };

  return (
    <div className="service-info">
      <div className="header">
        <span className="title">{details?.serviceName}</span>
        {details?.serviceType === "APPOINTMENT" && (
          <span className="time-badge">{details?.serviceTime}분</span>
        )}
      </div>
      <div>
        <span className="additional-info">{getServiceType(details?.serviceType)}</span>
        <span className="additional-info middot">{getUsePeriod(details)}</span>
        <span className="additional-info middot">{getUseNumber(details)}</span>
      </div>

      <div className="divider" />

      <div className="wrapper">
        <span className="gray">이용권 기간</span>
        <span className="dark date">{formatPassDate()}</span>
      </div>
      {details?.serviceType === "APPOINTMENT" && (
        <Fragment>
          <div className="wrapper">
            <span className="gray">담당강사</span>
            <span className="dark">{details?.coachName}</span>
          </div>
          <div className="wrapper">
            <span className="gray">장소</span>
            <span className="dark">{details?.spaceName}</span>
          </div>
        </Fragment>
      )}
    </div>
  );
});

const PriceInfo = memo(({ details, packageInfo, discount, onDiscountChange }: any) => {
  const price = details?.packagePass === "Y" ? packageInfo?.packagePrice : details?.price;

  return (
    <div className="price-info">
      <div className="wrapper margin-bottom">
        <span className="black">이용권 금액</span>
        <span className="dark">{moneyFormatter(price)}</span>
      </div>
      <div className="wrapper">
        <span className="black">할인 금액</span>
        <Input
          type="currency"
          onChange={(val: any) => onDiscountChange(val)}
          disabled={!details.discountPermission}
          maxPrice={price}
        />
      </div>
      <div className="point-info">
        <img src={point_info} />
        <span>포인트 적립은 센터(본점)의 정책대로 적립됩니다.</span>
      </div>

      <div className="divider" />

      <div className="wrapper">
        <span className="black bold">총 결제금액</span>
        <span className="blue bold">{moneyFormatter(discount ? price - discount : price)}</span>
      </div>
    </div>
  );
});

export default ConfirmPopup;
